<template>
  <div class="owal-element-header">
    <div class="owal-header-avatar__container">
      <cotenants
        :cotenants="cotenants"
        :tenant="currentIdentity"
        :unit="currentIdentity.unit"
        direction="row-reverse"
        @click="cotenantSelected"
      />
    </div>
    <h1>{{ titleCase(this.currentIdentity.person_name || this.currentIdentity.name || this.title) }}</h1>
    <div class="owal-tags">
      <div
        v-for="tag in this.tags"
        :key="tag"
        class="owal-tag"
        :class="{ clickable: tag.toLowerCase().startsWith('unit')}"
        @click="tagClicked(tag)"
      >
        {{ tag }}
      </div>
      <div
        v-if="currentIdentity && !(enoughTags)"
        class="owal-tag orange"
      >
        Needs Training
      </div>
    </div>
  </div>
</template>

<script>
import { hasEnoughTags } from '@/libraries/util/hasEnoughTags'
import { mapGetters } from 'vuex'
import { OwalPanelHeader } from '@/libraries/owal-ui'

import Cotenants from '@/components/shared/UnitCotenants'
import { imageForCredentialUUID, urlForIdentityId } from '@/libraries/api/faces'
import { titleCase } from '@/libraries/util/string'

/**
 * @class FacesDetailsPanelHeader A component for displaying information about an identity.
 * @extends OwalPanelHeader
 */
export default {
  name: 'FacesDetailsProfileHeader',
  extends: OwalPanelHeader,
  props: {
    /** @prop {Array<String>} tags An array of tags to be displayed in the interface. */
    tags: {
      type: Array,
      default: () => [],
    },
    /** @prop {Object} currentIdentity An identity to display in the header. */
    currentIdentity: {
      type: Object,
      default: undefined,
    },
  },
  computed: {
    ...mapGetters({
      cotenants: 'credentialMismatch/selectedIdentityCotenants',
    }),
    enoughTags () {
      if (this.currentIdentity.wellTrained) {
        return this.currentIdentity.wellTrained
      } else return hasEnoughTags(this.currentIdentity)
    },
  },
  methods: {
    hasEnoughTags,
    imageForCredentialUUID,
    urlForIdentityId,
    titleCase,
    /** Determines if the tag contains information about the apartment.
     * @returns `true` if the first word in the tag is "unit" otherwise `false`.
     */
    tagIsUnitTag (tag) {
      return tag.toLowerCase().startsWith('unit')
    },
    /** Navigates when a cotenant is clicked on. */
    cotenantSelected (payload) {
      const components = this.$route.path.split('/').filter(component => component.length > 0)
      const cotenantId = payload.cotenant.identityId || payload.cotenant.frontend_face_uuid
      const path = `/${components[0]}/${cotenantId}`
      this.$router.push(path)
    },
    /** Emits a `button-click` when a tag is clicked. */
    tagClicked (tag) {
      if (this.tagIsUnitTag(tag)) {
        this.$emit('button-click', 'tag-button-unit')
      }
    },
  },
  components: {
    Cotenants,
  },
}
</script>

<style scoped>
  .owal-element-header {
    background: #c0ccda;
    color: #475669;
    padding: 36px;
    min-height: 145px;
  }

  .owal-element-header h1 {
    margin: 0;
    margin-top: -16px;
    font-family: 'Lato-Bold', 'Helvetica Neue Bold', 'HelveticaNeue-Bold',
      'Helvetica';
    font-size: 48pt;
  }

  .owal-element-header {
    flex-shrink: 0;
  }

  .owal-element-header .owal-header-avatar__container {
    float: right;
    display: flex;
    flex-direction: row-reverse;
    height: 7.1428571429rem;
    min-width: 7.1428571429rem;
    flex-shrink: 0;
    padding: 0;
    margin: 0;
    justify-content: center;
    align-items: center;
  }

  .avatar {
    display: block;
    background-color: #eff2f7;
    object-fit: cover;
    height: 7.1428571429rem;
    width: 7.1428571429rem;
    border-radius: 50%;
    margin: 0;
    padding: 0;
    z-index: 9;
  }

  /** F1 Score Confidence */
  .owal-element-header >>> .owal.indicator.confidence {
    /* margin-top: 6rem; */
    /* margin-left: -1rem; */
    z-index: 10;
    align-self: flex-end;
    margin: -1.5rem 0 0 -1.5rem;
  }

  .owal-element-header >>> .owal.indicator.confidence .circle {
    background: #f6aa88;
    color: #fff;
  }

  .owal-tag {
    background-color: #8492a6;
    color: #ffffff;
    font-family: 'Lato', 'HelveticaNeue', 'Helvetica';
    font-size: 16pt;
    display: inline-block;
    border-radius: 10px;
    padding: 10px;
  }

  .orange {
    background-color: #f6aa88;
  }

  .owal-tag + .owal-tag {
    margin: 2px 8px;
  }

  /** If a tag is clickable, add some affordances. */
  .clickable {
    cursor: pointer;
    transition: all 200ms ease;
  }

  .clickable:hover {
    transform: scale(1.01);
  }
</style>
